import {
  Bell,
  Building,
  Calendar,
  ChevronRight,
  Circle,
  Clock,
  FileText,
  Gift,
  MessageSquare,
  Scissors,
  Settings,
  Star,
  User,
  Users,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { UserNotification } from "../../types";
import {
  formatNotificationTime,
  formatReferenceInfo,
  getNotificationCategoryIcon,
  getReferenceTypeColor,
  getReferenceTypeIcon,
  hasSentSmsDelivery,
} from "../../utils";
import {
  handleNotificationNavigation,
  isNotificationActionable,
} from "../../utils/navigation-helpers";

interface NotificationCardMobileProps {
  notification: UserNotification;
  onMarkAsRead?: (id: string) => void;
  onNavigate?: (path: string) => void;
  onOpenDialog?: (type: string, id: string, params?: Record<string, any>) => void;
  onSwipeLeft?: (id: string) => void;
  onSwipeRight?: (id: string) => void;
  className?: string;
}

const IconMap = {
  Calendar,
  Star,
  Gift,
  Settings,
  Bell,
  Clock,
  Users,
  User,
  Scissors,
  Building,
  FileText,
  Circle,
  MessageSquare,
};

export function NotificationCardMobile({
  notification,
  onMarkAsRead,
  onNavigate,
  onOpenDialog,
  onSwipeLeft: _onSwipeLeft,
  onSwipeRight: _onSwipeRight,
  className,
}: NotificationCardMobileProps) {
  const timeAgo = formatNotificationTime(notification.createdAt);
  const categoryIconName = getNotificationCategoryIcon(notification.category);
  const CategoryIcon = IconMap[categoryIconName as keyof typeof IconMap] || Bell;

  const handleAction = () => {
    // Mark as read immediately when clicked
    if (!notification.isRead && onMarkAsRead) {
      onMarkAsRead(notification.id);
    }

    // Use the new navigation system with dialog support
    handleNotificationNavigation(notification, onNavigate, onOpenDialog);
  };

  const isActionable = isNotificationActionable(notification);
  const _referenceInfo = formatReferenceInfo(notification.referenceType, notification.referenceId);
  const referenceIconName = notification.referenceType
    ? getReferenceTypeIcon(notification.referenceType)
    : null;
  const _ReferenceIcon = referenceIconName
    ? IconMap[referenceIconName as keyof typeof IconMap]
    : null;
  const _referenceColor = notification.referenceType
    ? getReferenceTypeColor(notification.referenceType)
    : "";

  return (
    <div
      className={cn(
        "group relative touch-manipulation",
        "border-b border-border last:border-b-0",
        isActionable && "cursor-pointer",
        className
      )}
      onClick={isActionable ? handleAction : undefined}
    >
      {/* Main content - optimized for touch */}
      <div className="flex items-center gap-3 p-3 min-h-[48px]">
        {/* Category indicator */}
        <div className="flex-shrink-0">
          <CategoryIcon className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Content - optimized for single line */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3
              className={cn(
                "text-sm font-medium truncate",
                !notification.isRead && "font-semibold text-foreground",
                notification.isRead && "text-muted-foreground"
              )}
            >
              {notification.title}
            </h3>

            {/* Unread indicator */}
            {!notification.isRead && (
              <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
            )}
          </div>

          {/* Compact metadata row */}
          <div className="flex items-center gap-2 mt-1">
            <span className="text-xs text-muted-foreground">{timeAgo}</span>

            {/* SMS delivery indicator - compact */}
            {hasSentSmsDelivery(notification) && (
              <MessageSquare className="h-3 w-3 text-green-600" />
            )}
          </div>
        </div>

        {/* Action indicator */}
        {isActionable && (
          <div className="flex-shrink-0 ml-auto">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAction();
                    }}
                    className="h-7 w-7 p-0"
                  >
                    <ChevronRight className="h-4 w-4" />
                    <span className="sr-only">View notification details</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>View details</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        )}
      </div>
    </div>
  );
}
